import React, { useState, useEffect, useCallback } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import "../../styles/Admin.css";
import AdminFooter from "../common/AdminFooter";
import AdminSideBar from "./AdminSideBar";
import getUserMenuItems from "./AdminMenuContent";
import { api, adminReqConfig } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import Spinner from "../add-ons/Spinner";
import "../../styles/CategoryProductList.css";
import { ERROR, SUCCESS } from "../../include/constant";
import { useNavigate } from "react-router-dom";
import {
  getAdmin,
  isEmptyObj,
  req,
  tooLong,
  validateEmail,
  validatePhoneNumber,
} from "../../include/function";

const AddUser = ({ type }) => {
  const adminUser = getAdmin();
  const toast = useNotification();
  const navigate = useNavigate();
  const homeLink = "/admin";
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();

  // URL parsing
  const urlParams = new URLSearchParams(window.location.search);
  const userIdFromUrl = urlParams.get("id");
  const isViewMode = urlParams.get("view") === "true";
  const isEditMode = !!userIdFromUrl && !isViewMode;

  // State
  const [saving, setSaving] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(isEditMode || isViewMode);
  const [roles, setRoles] = useState([]);
  const [user, setUser] = useState({});
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailId, setEmailId] = useState("");
  const [role, setRole] = useState("");
  const [userIdError, setUserIdError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [nameError, setNameError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [emailIdError, setEmailIdError] = useState("");
  const [roleError, setRoleError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState("active"); // Default active
  const [statusError, setStatusError] = useState("");

  // BUTTON STYLING HELPERS
  const getButtonStyle = (isPrimary = false) => ({
    border: "none",
    padding: "8px 20px",
    fontWeight: "500",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    ...(isPrimary 
      ? { 
          backgroundColor: saving ? "#1565c0" : "#1976d2", 
          color: "#fff" 
        } 
      : { 
          backgroundColor: saving ? "#e0e0e0" : "#bed9f8ff", 
          color: saving ? "#9e9e9e" : "#1976d2" 
        }
    ),
  });

  const renderLoadingSpinner = (size = "sm") => (
    <i 
      className={`fa fa-spinner fa-spin ${size === "sm" ? "me-2" : "me-3"}`} 
      aria-hidden="true"
      style={{ fontSize: size === "sm" ? "0.9rem" : "1.1rem" }}
    ></i>
  );

  useEffect(() => {
    api
      .get("/role/", adminReqConfig())
      .then((response) => {
        const allRoles = response.data.data || [];
        const validRoles = allRoles.filter((role) => role.id !== -1);
        setRoles(validRoles);
      })
      .catch((error) => {
        console.error("Error fetching roles", error);
        toast({ type: ERROR, message: "Failed to load roles." });
      });
  }, []);

  // Fetch user (for both Edit & View)
  const getUser = useCallback(() => {
    if (!userIdFromUrl) {
      setFetchingUser(false);
      return;
    }

    setFetchingUser(true);
    api
      .get(`/user/?id=${userIdFromUrl}`, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          const userData = Array.isArray(response.data.data)
            ? response.data.data[0]
            : response.data.data;
          setUser(userData || {});
        }
      })
      .catch((error) => {
        setUser({});
        toast({ type: ERROR, message: "Failed to load user." });
      })
      .finally(() => setFetchingUser(false));
  }, [userIdFromUrl]);

  // Trigger fetch in Edit or View
  useEffect(() => {
    if (isEditMode || isViewMode) {
      getUser();
    }
  }, [isEditMode, isViewMode, getUser]);

  // Populate form fields when user data loads
  useEffect(() => {
    if (!isEmptyObj(user)) {
      setUserId(user.userId || "");
      setName(user.name || "");
      setPhoneNumber(user.phoneNumber || "");
      setEmailId(user.emailId || "");
      setRole(user.roleId || "");
      setStatus(user.status || "active"); // Set status from user data
    }
  }, [user]);

  const clearFields = () => {
    if (saving) return;
    setUserId("");
    setPassword("");
    setName("");
    setPhoneNumber("");
    setEmailId("");
    setRole("");
    setConfirmPassword("");
    setStatus("active"); // Reset to default active
    setUserIdError("");
    setPasswordError("");
    setNameError("");
    setPhoneNumberError("");
    setEmailIdError("");
    setRoleError("");
    setConfirmPasswordError("");
    setStatusError("");
  };

  const handleSave = () => {
    let error = false;

    const data = {
      userId: String(userId).trim().toLowerCase(),
      password: String(password).trim(),
      name: String(name).trim(),
      phoneNumber: String(phoneNumber).trim(),
      emailId: String(emailId).trim().toLowerCase(),
      shopId: Number(1),
      roleId: String(role).trim(),
      status: status, // Add status
    };

    // Validation
    if (!isEditMode) {
      if (!data.userId) {
        setUserIdError(req("Username"));
        error = true;
      } else if (data.userId.length > 100) {
        setUserIdError(tooLong("Username"));
        error = true;
      }

      if (!data.password) {
        setPasswordError("Password required");
        error = true;
      } else if (data.password.length < 8) {
        setPasswordError("Password must be at least 8 characters");
        error = true;
      }

      if (!confirmPassword) {
        setConfirmPasswordError("Confirm password required");
        error = true;
      } else if (data.password !== confirmPassword) {
        setConfirmPasswordError("Passwords do not match");
        error = true;
      }
    }

    if (!data.name) {
      setNameError(req("Name"));
      error = true;
    } else if (data.name.length > 500) {
      setNameError(tooLong("Name"));
      error = true;
    }

    if (!data.phoneNumber) {
      setPhoneNumberError("Phone number required");
      error = true;
    } else if (!validatePhoneNumber(data.phoneNumber)) {
      setPhoneNumberError("Invalid phone number");
      error = true;
    }

    if (!data.emailId) {
      setEmailIdError("Email address required");
      error = true;
    } else if (!validateEmail(data.emailId)) {
      setEmailIdError("Invalid email address");
      error = true;
    }

    if (!data.roleId) {
      setRoleError("Role is required");
      error = true;
    }

    if (!status) {
      setStatusError("Status is required");
      error = true;
    }

    if (error) return;

    // API call
    setSaving(true);
    const apiCall = !isEditMode ? api.post : api.put;
    const endpoint = !isEditMode ? "/user/" : `/user/?id=${userIdFromUrl}`;
    apiCall(endpoint, data, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          toast({ type: SUCCESS, message: response.data.message });
          navigate("/admin/user/userList");
          if (!isEditMode) clearFields();
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message: error.response?.data?.message || "Failed to save user.",
        });
      })
      .finally(() => setSaving(false));
  };

  const inputStyle = {
    borderRadius: "5px",
    fontWeight: "400",
    outline: "none",
    width: "100%",
    padding: "11px 40px 11px 12px",
    backgroundColor: "transparent",
    border: "1px solid var(--gray-100)",
    color: "hsl(var(--black))",
    opacity: saving ? 0.7 : 1,
    pointerEvents: saving ? "none" : "auto",
  };

  const getPasswordStrength = (pwd) => {
    if (pwd.length === 0) return "";
    if (pwd.length < 8) return "Weak";
    if (/[A-Z]/.test(pwd) && /[0-9]/.test(pwd) && /[^A-Za-z0-9]/.test(pwd)) {
      return "Strong";
    }
    return "Moderate";
  };

  return (
    <>
      <AdminSideBar
        menuContent={menuContent}
        isMinimized={isSidebarMinimized}
      />
      <AdminTopNavBar
        toggleSidebar={toggleSidebar}
        isSidebarMinimized={isSidebarMinimized}
        homeLink={homeLink}
        title={
          isViewMode ? "USER DETAILS" : isEditMode ? "EDIT USER" : "ADD USER"
        }
      />
      <div
        className="content-wrapper p-0"
        style={{
          marginLeft: isSidebarMinimized ? "80px" : "250px",
          flex: "1",
        }}
      >
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
          }}
        >
          {fetchingUser ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "50vh" }}
            >
              <Spinner color="primary" />
            </div>
          ) : (
            <div className="container-fluid">
              <div className="row">
                <div className="col-lg-12">
                  <div className={`card border-0 ${isViewMode ? "py-20" : ""}`}>
                    <div className="d-flex justify-content-between align-items-center p-16">
                      <h6 className="mb-0 fs-5">User Information</h6>
                      {!isViewMode && (
                        <button
                          onClick={clearFields}
                          disabled={saving}
                          style={getButtonStyle(false)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Clearing...
                            </>
                          ) : (
                            "Clear"
                          )}
                        </button>
                      )}
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6 mb-16">
                          <h6 className="text-md mb-16 fw-bold">User ID</h6>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter User ID"
                            value={userId}
                            onChange={(e) => {
                              if (saving) return;
                              setUserId(e.target.value);
                              setUserIdError("");
                            }}
                            readOnly={isViewMode || isEditMode} 
                            disabled={saving}
                            style={{
                              ...inputStyle,
                              backgroundColor:
                                isViewMode || isEditMode
                                  ? "#f5f5f5"
                                  : "transparent",
                              cursor:
                                (isViewMode || isEditMode) && !saving
                                  ? "not-allowed"
                                  : "auto",
                            }}
                          />
                          {userIdError && !isViewMode && (
                            <div className="text-danger mt-1">
                              {userIdError}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-16">
                          <h6 className="text-md mb-16 fw-bold">User Name</h6>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter User Name"
                            value={name}
                            onChange={(e) => {
                              if (saving) return;
                              setName(e.target.value);
                              setNameError("");
                            }}
                            readOnly={isViewMode}
                            disabled={saving || isViewMode}
                            style={inputStyle}
                          />
                          {nameError && !isViewMode && (
                            <div className="text-danger mt-1">{nameError}</div>
                          )}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-16">
                          <h6 className="text-md mb-16 fw-bold">Email ID</h6>
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Enter Email ID"
                            value={emailId}
                            onChange={(e) => {
                              if (saving) return;
                              setEmailId(e.target.value);
                              setEmailIdError("");
                            }}
                            readOnly={isViewMode}
                            disabled={saving || isViewMode}
                            style={inputStyle}
                          />
                          {emailIdError && !isViewMode && (
                            <div className="text-danger mt-1">
                              {emailIdError}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-16">
                          <h6 className="text-md mb-16 fw-bold">
                            Phone Number
                          </h6>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Phone Number"
                            value={phoneNumber}
                            onChange={(e) => {
                              if (saving) return;
                              setPhoneNumber(e.target.value);
                              setPhoneNumberError("");
                            }}
                            readOnly={isViewMode}
                            disabled={saving || isViewMode}
                            style={inputStyle}
                          />
                          {phoneNumberError && !isViewMode && (
                            <div className="text-danger mt-1">
                              {phoneNumberError}
                            </div>
                          )}
                        </div>
                      </div>

                      {!isViewMode && !isEditMode && (
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Password</h6>
                            <div
                              className="position-relative"
                              style={{ width: "100%" }}
                            >
                              <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter Password"
                                className="form-control"
                                style={inputStyle}
                                value={password}
                                onChange={(e) => {
                                  if (saving) return;
                                  setPassword(e.target.value);
                                  setPasswordError("");
                                }}
                                disabled={saving}
                              />
                              <i
                                className={`toggle-password cursor-pointer ph ${
                                  showPassword ? "ph-eye" : "ph-eye-slash"
                                }`}
                                onClick={() => !saving && setShowPassword(!showPassword)}
                                style={{
                                  position: "absolute",
                                  right: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  fontSize: "18px",
                                  color: saving ? "#ccc" : "var(--gray-400)",
                                  cursor: saving ? "not-allowed" : "pointer",
                                  opacity: saving ? 0.5 : 1,
                                }}
                              />
                            </div>
                            <div
                              className="d-flex justify-content-between mt-1"
                              style={{ fontSize: "14px" }}
                            >
                              <span style={{ fontWeight: "600" }}>
                                At least 8 characters
                              </span>
                              <span
                                style={{
                                  color:
                                    getPasswordStrength(password) === "Strong"
                                      ? "green"
                                      : getPasswordStrength(password) ===
                                          "Moderate"
                                        ? "orange"
                                        : "red",
                                  fontWeight: "600",
                                }}
                              >
                                {getPasswordStrength(password)}
                              </span>
                            </div>
                            {passwordError && (
                              <div className="text-danger mt-1">
                                {passwordError}
                              </div>
                            )}
                          </div>

                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">
                              Confirm Password
                            </h6>
                            <div
                              className="position-relative"
                              style={{ width: "100%" }}
                            >
                              <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Enter Confirm Password"
                                className="form-control"
                                style={inputStyle}
                                value={confirmPassword}
                                onChange={(e) => {
                                  if (saving) return;
                                  setConfirmPassword(e.target.value);
                                  setConfirmPasswordError("");
                                }}
                                disabled={saving}
                              />
                              <i
                                className={`toggle-password cursor-pointer ph ${
                                  showConfirmPassword
                                    ? "ph-eye"
                                    : "ph-eye-slash"
                                }`}
                                onClick={() => !saving && setShowConfirmPassword(!showConfirmPassword)}
                                style={{
                                  position: "absolute",
                                  right: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  fontSize: "18px",
                                  color: saving ? "#ccc" : "var(--gray-400)",
                                  cursor: saving ? "not-allowed" : "pointer",
                                  opacity: saving ? 0.5 : 1,
                                }}
                              />
                            </div>
                            <div
                              className="d-flex justify-content-between mt-1"
                              style={{ fontSize: "14px" }}
                            >
                              <span style={{ fontWeight: "600" }}>
                                At least 8 characters
                              </span>
                              <span
                                style={{
                                  color:
                                    getPasswordStrength(confirmPassword) ===
                                    "Strong"
                                      ? "green"
                                      : getPasswordStrength(confirmPassword) ===
                                          "Moderate"
                                        ? "orange"
                                        : "red",
                                  fontWeight: "600",
                                }}
                              >
                                {getPasswordStrength(confirmPassword)}
                              </span>
                            </div>
                            {confirmPasswordError && (
                              <div className="text-danger mt-1">
                                {confirmPasswordError}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="row">
                        <div className="col-md-6 mb-16">
                          <h6 className="text-md mb-16 fw-bold">Role</h6>
                          {isViewMode ? (
                            <input
                              type="text"
                              className="form-control"
                              value={
                                roles.find((r) => r.id === role)?.roleName ||
                                "—"
                              }
                              readOnly
                              style={inputStyle}
                            />
                          ) : (
                            <select
                              className="form-select"
                              value={role}
                              onChange={(e) => {
                                if (saving) return;
                                setRole(e.target.value);
                                setRoleError("");
                              }}
                              disabled={saving}
                              style={{
                                ...inputStyle,
                                padding: "11px 24px",
                              }}
                            >
                              <option value="">Select Role</option>
                              {roles.map((r) => (
                                <option key={r.id} value={r.id}>
                                  {r.roleName}
                                </option>
                              ))}
                            </select>
                          )}
                          {roleError && !isViewMode && (
                            <div className="text-danger mt-1">{roleError}</div>
                          )}
                        </div>

                        <div className="col-md-6 mb-16">
                          <h6 className="text-md mb-16 fw-bold">Status</h6>
                          {isViewMode ? (
                            <input
                              type="text"
                              className="form-control"
                              value={
                                status === "active" ? "Active" : "Inactive"
                              }
                              readOnly
                              style={inputStyle}
                            />
                          ) : (
                            <select
                              className="form-select"
                              value={status}
                              onChange={(e) => {
                                if (saving) return;
                                setStatus(e.target.value);
                                setStatusError("");
                              }}
                              disabled={saving}
                              style={{
                                ...inputStyle,
                                padding: "11px 24px",
                              }}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          )}
                          {statusError && !isViewMode && (
                            <div className="text-danger mt-1">
                              {statusError}
                            </div>
                          )}
                        </div>
                      </div>

                      {isViewMode && (
                        <div className="d-flex justify-content-end">
                          <button
                            onClick={() => navigate("/admin/user/userList")}
                            style={{
                              backgroundColor: "#1976d2",
                              color: "#fff",
                              border: "none",
                              padding: "8px 20px",
                              fontWeight: "500",
                              borderRadius: "5px",
                            }}
                          >
                            Back to List
                          </button>
                        </div>
                      )}
                      {!isViewMode && (
                        <div className="d-flex justify-content-end gap-8 mt-16">
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            style={getButtonStyle(true)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                {isEditMode ? "Updating..." : "Adding..."}
                              </>
                            ) : isEditMode ? (
                              "Update"
                            ) : (
                              "Add"
                            )}
                          </button>
                          <button
                            onClick={clearFields}
                            disabled={saving}
                            style={getButtonStyle(false)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                Clearing...
                              </>
                            ) : (
                              "Clear"
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
      <AdminFooter isSidebarMinimized={isSidebarMinimized} />
    </>
  );
};

export default AddUser;